/**
 * Media Domain Types
 *
 * Pure TypeScript types for the media domain. No React imports.
 */

import {CancelToken} from "axios";

/**
 * Supported media types
 */
export type MediaType = "image" | "video" | "document";

/**
 * Offering types that can have media attached
 */
export type OfferingType =
  | "service"
  | "product-service"
  | "product"
  | "live-class"
  | "portfolio";

/**
 * Upload status for tracking upload progress
 */
export type UploadStatus = "pending" | "uploading" | "success" | "error" | "cancelled";

/**
 * Properties for creating a Media entity
 */
export interface MediaProps {
  id: string | number;
  url: string;
  type: MediaType;
  fileName?: string;
  fileSize?: number;
  offeringId?: number;
  offeringType?: OfferingType;
  userId?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Cover image domain type
 * Mapped from API response with domain-friendly field names
 */
export interface CoverImage {
  id: string;
  url: string;
  type: MediaType;
  offeringId: number;
  offeringType: OfferingType;
  userId: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Upload progress information
 */
export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Options for upload operations
 */
export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  cancelToken?: CancelToken;
}

/**
 * Parameters for uploading an image
 */
export interface UploadImageParams {
  file: File;
  offeringId: string | number;
  offeringType: OfferingType;
  options?: UploadOptions;
}

/**
 * Parameters for uploading content (binary files like PDFs, videos)
 */
export interface UploadContentParams {
  file: File;
  productId: string | number;
  fileName: string;
  fileExtension: string;
  options?: UploadOptions;
}

/**
 * Parameters for deleting media
 */
export interface DeleteMediaParams {
  mediaId: string | number;
}

/**
 * Result of an upload operation
 */
export interface UploadResult {
  media: MediaProps[];
}

/**
 * Query key configuration for cache invalidation
 */
export interface MediaQueryKeyConfig {
  queryKey: readonly unknown[];
}

/**
 * Offering-specific query keys used for cache invalidation
 */
export const OFFERING_QUERY_KEYS = {
  session: "COACH_SESSION_OFFERINGS",
  liveEvent: "live_classes",
  product: "digital_product",
  storeService: "store-service",
} as const;

/**
 * Query key for cover images cache
 */
export const COVER_IMAGES_QUERY_KEY = "cover-images" as const;
